import { Link } from '@tanstack/react-router';
import {
  PencilIcon,
  XMarkIcon,
  TagIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { DocsLayout } from '../../components/docs/DocsLayout.tsx';
import { MediaVideo } from '../../components/MediaVideo.tsx';
import { DocIcon } from '../../components/docs/DocIcon.tsx';
import { useDocumentTitle } from '../../hooks/useDocumentTitle.ts';
import { useT } from '../../i18n.ts';

export function DocsDetails() {
  const t = useT();
  useDocumentTitle(t('docs.details.title'));

  return (
    <DocsLayout
      path="/docs/details"
      title={t('docs.details.label')}
      lede="A document’s content is grouped into sections you can nest. This page covers the ways you work with them, from adding and moving to deleting, labeling, and guarding them with their own approvals."
    >
      <MediaVideo
        base="overview-details-software"
        label="A walkthrough of editing, organizing, and guarding the contents of a Goalpost document"
      />
      <h2 id="intro">How editing works</h2>
      <p>
        As you work, Goalpost keeps two views of your change batch at once: what the
        document now says, and the specific changes you made to it. That
        dual record is what makes review easy. An approver can read it as a clear
        list of changes rather than comparing two documents, and whoever later
        implements it can see exactly what changed, not just the final text.
      </p>
      <p>
        You edit the contents of a document while proposing a change. Open a document, select{' '}
        <strong>Propose Changes</strong> from the document nav, and each item
        shows a small row of actions. Everything below is done there, gathered
        into a <Link to="/docs/change-management">draft</Link> that becomes
        official once it’s submitted and approved.
      </p>

      <h2 id="edit">Edit contents</h2>
      <p>
        Editing an item changes its wording. The previous wording is preserved in
        the document’s history, so history can show “budget went from $12k to $15k”
        as a connected decision rather than a value that simply appeared.
      </p>
      <ol>
        <li>
          On the item, choose <strong>Edit detail</strong>{' '}
          <DocIcon><PencilIcon aria-hidden="true" /></DocIcon>.
        </li>
        <li>Update the text.</li>
        <li>
          Choose <strong>Save</strong>. The change joins your draft alongside your
          other changes; submit the draft when it’s ready.
        </li>
      </ol>

      <h2 id="move">Move contents</h2>
      <p>
        Moving an item changes its position among its siblings, or re-parents it
        to a new spot in the tree. An item carries its children with it, keeping
        their structure intact. Drag an item by its handle (
        <strong>Drag to reorder</strong>) to a new position, or move the focused
        item with <strong>Alt + arrow keys</strong>. Use this to regroup related
        items or to promote one that has outgrown its original parent.
      </p>

      <h2 id="delete">Delete contents</h2>
      <p>
        On the item, choose <strong>Remove detail</strong>{' '}
        <DocIcon><XMarkIcon aria-hidden="true" /></DocIcon>. Because a document is a tree, removing an item
        removes <strong>all of its children</strong> too, and Goalpost confirms
        this before it goes ahead (“This detail and all its children will be
        removed”). If there are children you want to keep, move them somewhere else
        first.
      </p>
      <p>
        A removal is a change like any other: it joins your draft and becomes
        official only once submitted and approved. And because approved versions
        are permanent, a removed item never disappears from history. You can
        always look back at a version where it still existed.
      </p>

      <h2 id="approval">Set an approval rule</h2>
      <p>
        An item can carry its own{' '}
        <Link to="/docs/approval-rules">approval rule</Link>, so a
        sensitive part of the tree requires stricter sign-off than the rest of the
        document. Any change that touches that item, or anything beneath it, then
        has to satisfy the item’s rule in addition to the document’s.
      </p>
      <ol>
        <li>
          On the item, choose <strong>Set approval rule</strong>{' '}
          <DocIcon><ShieldCheckIcon aria-hidden="true" /></DocIcon>.
        </li>
        <li>
          Pick one of the document’s rules, or choose <strong>None</strong> to
          clear it. The rule’s teams must be assigned to the document.
        </li>
        <li>Choose <strong>Save</strong>.</li>
      </ol>
      <p>
        The rule governs this item and everything under it, so descendants
        inherit it automatically. This is how a mostly lightweight document can
        still put a firm gate around its budget, its legal terms, or a decision
        everything else depends on.
      </p>

      <h2 id="labels">Labels</h2>
      <p>
        <strong>Labels</strong> are short tags you attach to items to organize
        and find them, such as “must-have,” “open question,” or “phase 2.” An item
        can carry several labels (up to 20), and you can add or remove them as the
        work evolves.
      </p>
      <ol>
        <li>
          On the item, choose <strong>Edit labels</strong>{' '}
          <DocIcon><TagIcon aria-hidden="true" /></DocIcon>.
        </li>
        <li>
          Type a label and press Enter to add it, or remove one with the{' '}
          <DocIcon><XMarkIcon aria-hidden="true" /></DocIcon> on its chip. Only workspace owners can
          define new labels; everyone else picks from the ones that already exist.
        </li>
        <li>Choose <strong>Save</strong>.</li>
      </ol>
      <p>
        Labels don’t change what an item says or how it’s approved. They’re there
        to help you filter and navigate a large document and to give everyone
        a shared shorthand for cross-cutting themes.
      </p>

      <h3>Managing the label vocabulary</h3>
      <p>
        The set of labels a document offers is managed on the document’s{' '}
        <strong>Settings</strong> page, under <strong>Labels</strong>. There, a
        workspace owner can add a label (type a name and choose{' '}
        <strong>Add Label</strong>) and archive labels that shouldn’t be used
        anymore. An archived label stays on the items that already carry it but
        can’t be attached to new ones; <strong>Restore</strong> brings it back.
        New labels can also be created inline while editing an item’s labels.
      </p>

      <h2 id="structure">Structuring a document</h2>
      <p>
        The tree <em>is</em> the structure. Goalpost keeps items in the order
        you put them and shows how they nest, so the structure doesn’t need to
        be repeated in the wording. A few habits keep a document easy to
        change and easy to review:
      </p>
      <ul>
        <li>
          <strong>Leave out section numbers.</strong> Title a section
          “Offerings” rather than “3. Offerings,” and its child “Storage
          services” rather than “3.1 Storage services.” Numbers typed into
          titles go stale as soon as something is added, moved, or removed
          above them, and fixing them means editing every item that follows.
          That turns a one-line change into a long draft for approvers to read
          and fills the history with edits to content that didn’t really
          change.
        </li>
        <li>
          <strong>Keep numbers that mean something.</strong> “Phase 2,” “Tier 3
          support,” or a clause from an outside standard are part of what the
          item says, not where it sits. Those stay.
        </li>
        <li>
          <strong>Nest instead of formatting.</strong> Rather than listing
          several points inside one item with dashes or indentation, give
          each its own child. One point per item means each can
          be edited, moved, labeled, and{' '}
          <a href="#approval">guarded</a> on its own.
        </li>
      </ul>
      <p>
        The same habits apply when an{' '}
        <Link to="/docs/ai-integration">AI assistant</Link> drafts changes for
        you; Goalpost’s instructions to assistants ask them to follow these
        too.
      </p>
    </DocsLayout>
  );
}
